/**
 * The one door media takes to a provider.
 *
 * Base64 handed to a model must decode, or the request 400s. The payload is
 * replayed from stored history, so every later turn 400s too. That is not
 * hypothetical: a stored screenshot can come back string-truncated (a row
 * cap, a compaction pass), and a truncated data URL still parses as one, so
 * shape alone is not evidence. {@link isDecodableBase64} is the evidence, and
 * anything failing it degrades to text, which no provider can reject.
 */

/** Standard base64: alphabet only, and a length `atob` accepts. */
const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;

export function isDecodableBase64(data: string): boolean {
  return data.length % 4 !== 1 && BASE64.test(data);
}

/** A `data:<media>;base64,<payload>` URL, split. */
const DATA_URL = /^data:([a-zA-Z0-9.+-]+\/[a-zA-Z0-9.+-]+);base64,(.+)$/s;

export interface Media {
  mediaType: string;
  data: string;
}

/** The media a data URL carries, or null when it isn't one the model can use. */
export function parseModelDataUrl(value: unknown): Media | null {
  const match = typeof value === "string" ? value.match(DATA_URL) : null;
  if (!match) return null;
  const [, mediaType, data] = match;
  if (!mediaType || !data || !isDecodableBase64(data)) return null;
  return { mediaType, data };
}

export type MediaModelPart =
  | { type: "image-data"; data: string; mediaType: string }
  | { type: "file-data"; data: string; mediaType: string }
  // A file the provider fetches for itself: what a stored reference resolves
  // to, signed per prompt build (`./model-images`).
  | { type: "image-url"; url: string }
  | { type: "file-url"; url: string }
  | { type: "text"; text: string };

const decodeUtf8 = (data: string) =>
  new TextDecoder().decode(Uint8Array.from(atob(data), (c) => c.charCodeAt(0)));

/**
 * One media blob as the content part a model reads. Extracted text arrives as
 * text; images as `image-data`; other binary (PDFs) as `file-data`, which
 * Anthropic maps to its document type.
 */
export function mediaModelPart(
  mediaType: string,
  data: string,
): MediaModelPart {
  if (!isDecodableBase64(data)) {
    return {
      type: "text",
      text: `[${mediaType} dropped: the stored payload is not decodable]`,
    };
  }
  if (mediaType === "text/plain")
    return { type: "text", text: decodeUtf8(data) };
  if (mediaType.startsWith("image/"))
    return { type: "image-data", data, mediaType };
  return { type: "file-data", data, mediaType };
}

/**
 * A file the provider reads by URL, as its type says: a picture as an image,
 * anything else (a PDF) as a file. A data URL, which is all a host with no
 * object storage can point at, goes inline instead.
 */
export function urlModelPart(mediaType: string, url: string): MediaModelPart {
  const inline = parseModelDataUrl(url);
  if (inline) return mediaModelPart(inline.mediaType, inline.data);
  return mediaType.startsWith("image/")
    ? { type: "image-url", url }
    : { type: "file-url", url };
}
